import dayjs, { Dayjs } from 'dayjs';
import {
  ALIMTALK_SEND_HISTORY_DEFAULT_PAGE_SIZE,
  ALIMTALK_SEND_HISTORY_MAX_PAGE,
  ALIMTALK_SEND_HISTORY_PAGE_SIZE_OPTIONS,
} from './constants';
import { ALIMTALK_SEND_STATUS, ALIMTALK_SEND_TYPE, AlimtalkSendHistorySearchType } from './types';
import { AlimtalkSendHistoryFilter, DEFAULT_ALIMTALK_SEND_HISTORY_FILTER, isInvalidScheduleIdSearch } from './utils';

/**
 *  발송 내역 목록 URL 쿼리 ↔ (필터 + 페이지) 변환.
 *
 *  목록 → 상세 → 뒤로가기/목록으로 이동해도 검색 조건·페이지가 남도록 URL 이 상태의 정본이다.
 *  사용자가 주소창을 직접 고칠 수 있으므로 parse 는 던지지 않고, 못 읽는 값은 항목별로 기본값으로 떨어뜨린다.
 *  (레포 관례: components/list/useListState.ts, views/promotion/schedule/utils/scheduleListQuery.ts)
 *
 *  날짜는 달력의 연-월-일(YYYY-MM-DD)만 싣는다. 조회 시 toListQueryParams 가 이 날짜를 KST 하루 경계로 해석하므로
 *  URL 에 시각·시간대를 싣지 않아도 브라우저 시간대와 무관하게 같은 조건이 된다.
 */

export interface AlimtalkSendHistoryListState {
  filter: AlimtalkSendHistoryFilter;
  page: number;
  pageSize: number;
}

const URL_DATE_FORMAT = 'YYYY-MM-DD';
const URL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const SEARCH_VALUE_MAX_LENGTH = 500;

const STATUS_VALUES: string[] = Object.values(ALIMTALK_SEND_STATUS);
const SEND_TYPE_VALUES: string[] = Object.values(ALIMTALK_SEND_TYPE);
const SEARCH_TYPE_VALUES: AlimtalkSendHistorySearchType[] = ['scheduleId', 'memo', 'adminName'];

/** 형식뿐 아니라 달력 유효성까지 본다 (2026-02-30 은 버린다) */
const parseDate = (raw: string | null): Dayjs | null => {
  if (!raw || !URL_DATE_PATTERN.test(raw)) return null;
  const date = dayjs(raw);
  return date.isValid() && date.format(URL_DATE_FORMAT) === raw ? date : null;
};

const parseDateRange = (params: URLSearchParams): AlimtalkSendHistoryFilter['dateRange'] => {
  const start = parseDate(params.get('startDate'));
  const end = parseDate(params.get('endDate'));
  if (!start && !end) return null;
  // 시작이 종료보다 늦은 범위는 달력에서 만들 수 없는 조건이라 기간 조건을 통째로 버린다
  if (start && end && start.isAfter(end)) return null;
  return [start, end];
};

const parseEnum = <T extends string>(raw: string | null, allowed: string[], fallback: T): T =>
  raw !== null && allowed.includes(raw) ? (raw as T) : fallback;

const parsePage = (raw: string | null): number => {
  const value = Number(raw);
  return Number.isSafeInteger(value) && value > 0 && value <= ALIMTALK_SEND_HISTORY_MAX_PAGE ? value : 1;
};

/** 페이저 선택지에 없는 값(?pageSize=7, ?pageSize=100000)은 기본값으로 — 백엔드 최대 100 */
const parsePageSize = (raw: string | null): number => {
  const value = Number(raw);
  return (ALIMTALK_SEND_HISTORY_PAGE_SIZE_OPTIONS as readonly number[]).includes(value)
    ? value
    : ALIMTALK_SEND_HISTORY_DEFAULT_PAGE_SIZE;
};

export const parseListSearchParams = (params: URLSearchParams): AlimtalkSendHistoryListState => {
  const defaults = DEFAULT_ALIMTALK_SEND_HISTORY_FILTER;
  const searchType = parseEnum<AlimtalkSendHistorySearchType>(
    params.get('searchType'),
    SEARCH_TYPE_VALUES,
    defaults.searchType,
  );
  const rawSearchValue = params.get('searchValue')?.trim() ?? '';
  const searchValue = rawSearchValue.length <= SEARCH_VALUE_MAX_LENGTH ? rawSearchValue : '';

  const filter: AlimtalkSendHistoryFilter = {
    dateBasis: defaults.dateBasis,
    dateRange: parseDateRange(params),
    status: parseEnum(params.get('status'), STATUS_VALUES, defaults.status),
    sendType: parseEnum(params.get('sendType'), SEND_TYPE_VALUES, defaults.sendType),
    searchType,
    searchValue,
  };

  // 숫자가 아닌 예약 ID 는 백엔드가 400 으로 거절하므로 검색어만 버린다 (검색 박스와 같은 기준)
  if (isInvalidScheduleIdSearch(filter)) {
    filter.searchValue = '';
  }

  return {
    filter,
    page: parsePage(params.get('page')),
    pageSize: parsePageSize(params.get('pageSize')),
  };
};

/** 기본값과 같은 항목은 싣지 않아 URL 을 짧게 유지한다 */
export const serializeListSearchParams = ({
  filter,
  page,
  pageSize,
}: AlimtalkSendHistoryListState): Record<string, string> => {
  const defaults = DEFAULT_ALIMTALK_SEND_HISTORY_FILTER;
  const [start, end] = filter.dateRange ?? [null, null];
  const searchValue = filter.searchValue.trim();
  const next: Record<string, string> = {};

  if (start) next.startDate = start.format(URL_DATE_FORMAT);
  if (end) next.endDate = end.format(URL_DATE_FORMAT);
  if (filter.status) next.status = filter.status;
  if (filter.sendType) next.sendType = filter.sendType;
  if (filter.searchType !== defaults.searchType) next.searchType = filter.searchType;
  if (searchValue) next.searchValue = searchValue;
  if (page !== 1) next.page = String(page);
  if (pageSize !== ALIMTALK_SEND_HISTORY_DEFAULT_PAGE_SIZE) next.pageSize = String(pageSize);

  return next;
};

/**
 *  목록 → 상세 이동 시 목록의 쿼리 문자열을 history state 로 넘긴다.
 *  상세의 「목록으로」 버튼이 이 값으로 같은 조건·페이지의 목록에 돌아간다 (브라우저 뒤로가기는 URL 로 복원된다).
 */
export interface AlimtalkSendHistoryDetailLocationState {
  listSearch: string;
}

export const toDetailLocationState = (listSearch: string): AlimtalkSendHistoryDetailLocationState => ({
  listSearch,
});

/** history state 에서 목록 쿼리 문자열을 꺼낸다 — 직접 진입·새 탭 등 state 가 없거나 형식이 다르면 '' (기본 목록) */
export const readListSearchFromLocationState = (state: unknown): string => {
  if (typeof state !== 'object' || state === null) return '';
  const { listSearch } = state as Partial<AlimtalkSendHistoryDetailLocationState>;
  return typeof listSearch === 'string' && (listSearch === '' || listSearch.startsWith('?')) ? listSearch : '';
};
